import { Box, FormControlLabel, Grid, MenuItem, Paper, Radio, RadioGroup, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useField } from "@unform/core";
import { ToastContent } from "../../../../shared/components";
import LoadingDialog from "../../../../shared/components/LoadingModalDialog/LoadingModalDialog";
import { VTextField } from "../../../../shared/forms";
import { VMaskTextField } from "../../../../shared/forms/VMaskTextField";
import { useToast } from "../../../../shared/hooks";
import { PeopleService } from "../../../../shared/services/api/people/PeopleService";
import { globalColorTytle, globalMaskDocument, RangeStates, globalTranslateMaritalStatusCapitalization } from "../../../../shared/utils";

interface ISectionProps {
    data?: any;
    formRef: any;
}

export const CapitalizationTenantSection: React.FC<ISectionProps> = ({ data, formRef }) => {
    const [isLoadingDocument, setLoadingDocument] = useState(false);
    const [lastTenantDocument, setLastTenantDocument] = useState("");
    const toastHandler = useToast();

    // Register tenantPpe field
    const { fieldName, registerField, defaultValue, error, clearError } = useField("tenantPpe");
    const [tenantPpeValue, setTenantPpeValue] = useState<boolean>(defaultValue ?? false);

    useEffect(() => {
        registerField({
            name: fieldName,
            getValue: () => tenantPpeValue,
            setValue: (_, newValue) => setTenantPpeValue(newValue),
            clearValue: () => setTenantPpeValue(false),
        });
    }, [registerField, fieldName, tenantPpeValue]);

    const showToastError = (message: string) => {
        toastHandler.present({
            type: "error",
            position: "top-right",
            messageOrContent: <ToastContent title="Erro" content={message} />,
        });
    };

    const handleCPF = (value: string) => {
        const documento = value.replace(/\D/g, "").trim();

        if (documento === lastTenantDocument) return;
        if (documento.length < 11) return; // CPF incompleto

        setLastTenantDocument(documento);
        setLoadingDocument(true);

        PeopleService.getPersonDataByDocument(documento)
            .then((data) => {
                if (!data) {
                    showToastError(`Documento ${globalMaskDocument(value)} não localizado. Verifique se está correto.`);
                    setLoadingDocument(false);
                } else {
                    const { name, birthDate, motherName, email, phone } = data.naturalPersonData || {};
                    formRef.current?.setFieldValue("tenantName", name || "");
                    formRef.current?.setFieldValue("tenantBirthDate", birthDate || "");
                    // Se houver mais campos retornados pela API, mapear aqui
                    setLoadingDocument(false);
                }
            })
            .catch(() => {
                showToastError(`Erro na pesquisa do documento ${globalMaskDocument(value)}`);
                setLoadingDocument(false);
            });
    };

    return (
        <>
            <LoadingDialog isLoading={isLoadingDocument} loadingText={`Localizando documento...`} />
            <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">
                <Grid container direction="column" padding={2} spacing={2}>
                    <Grid item xs={11}>
                        <Typography variant="h6" color={globalColorTytle}>
                            Dados do Locatário
                        </Typography>
                    </Grid>

                    <Grid container item direction="row" spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <VMaskTextField
                                fullWidth
                                label="CPF/CNPJ*"
                                name="tenantDocument"
                                mask="cpf"
                                onBlur={(e) => handleCPF(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <VTextField fullWidth label="Nome*" name="tenantName" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <VTextField fullWidth label="Gênero*" name="tenantGender" select>
                                <MenuItem value="male">Masculino</MenuItem>
                                <MenuItem value="female">Feminino</MenuItem>
                                <MenuItem value="other">Outro</MenuItem>
                            </VTextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <VTextField fullWidth label="Data de nascimento*" name="tenantBirthDate" type="date" InputLabelProps={{ shrink: true }} />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <VTextField fullWidth label="Email*" name="tenantEmail" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <VMaskTextField fullWidth label="Telefone*" name="tenantPhone" mask="cellphone" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <VTextField fullWidth label="Estado Civil*" name="tenantMaritalStatus" select>
                                {["single", "married", "widower", "divorced", "stable_union"].map((status) => (
                                    <MenuItem key={status} value={status}>
                                        {globalTranslateMaritalStatusCapitalization(status)}
                                    </MenuItem>
                                ))}
                            </VTextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <VTextField fullWidth label="Nacionalidade" name="tenantNationality" />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <VTextField fullWidth label="Identidade*" name="tenantIdentity" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <VTextField fullWidth label="Órgão Emissor*" name="tenantIssuer" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <VTextField fullWidth label="UF*" name="tenantState" select>
                                {RangeStates.map(({ value, label }) => (
                                    <MenuItem key={value} value={value}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </VTextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <VTextField fullWidth label="Data de Emissão*" name="tenantIssueDate" type="date" InputLabelProps={{ shrink: true }} />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <VTextField fullWidth label="Profissão*" name="tenantProfession" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <VTextField fullWidth label="Renda mensal*" name="tenantMonthlyIncome" select>
                                <MenuItem value="R0">Não informado</MenuItem>
                                <MenuItem value="R2500">Até R$ 2.500,00</MenuItem>
                                <MenuItem value="R5000">De R$2.500,01 a R$5.000,00</MenuItem>
                                <MenuItem value="R10000">De R$5.000,01 a R$10.000,00</MenuItem>
                                <MenuItem value="R20000">acima de R$10.000,00</MenuItem>
                            </VTextField>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                Pessoa politicamente exposta?*
                            </Typography>
                            <RadioGroup
                                row
                                name="tenantPpe"
                                value={tenantPpeValue ? "true" : "false"}
                                onChange={(e) => {
                                    const newVal = e.target.value === "true";
                                    setTenantPpeValue(newVal);
                                    if (error) clearError();
                                }}
                            >
                                <FormControlLabel value="true" control={<Radio size="small" />} label="Sim" />
                                <FormControlLabel value="false" control={<Radio size="small" />} label="Não" />
                            </RadioGroup>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};
